<script>
import { PLAYLIST_CONFIGS } from '../playlist/configs.js';

let playlist = null;

function getStateClass (state) {
  if (state === 'preloaded') {
    return 'state-good';
  }
  if (state === 'preload-failed') {
    return 'state-bad';
  }
  return 'state-neutral';
}

function convertItem (item) {
  if (!item) return null;
  return {
    ...item,
    player: null,
    elIndex: item.player ? item.player.getSurface().getUniqueCssClass() : '--',
    index: item.index,
    title: item.config.metadata?.title ?? '--',
    stateClass: getStateClass(item.state),
  };
}

function onPlaylistError (error) {
  console.error('Playlist error', error);
}

function onPlaybackError (error) {
  console.error('Playback error', error);
}

export default {
  name: 'clpp-playlist',
  template: `
    <section class="playlist-preview">
      <h2>Multi-Controller (MC) Playlist</h2>

      <div>
        <button class="button" @click="playPrev" :disabled="!canPlayPrev">&lt;&lt; Prev</button>
        <button class="button" @click="playFirst" :disabled="!canPlayFirst">Play first</button>
        <button class="button" @click="playNext" :disabled="!canPlayNext">Next &gt;&gt;</button>
        <button class="button" @click="seekToEnd" :disabled="!canPlayNext">Seek to End -&gt;</button>
        <button class="button" @click="seekAfterEnd" :disabled="!canPlayNext">Seek After End -:-&gt;</button>
        <button class="button destroy" @click="destroy" :disabled="!canDestroy">Destroy</button>
      </div>

      <div class="anchor-container">
        <div class="video-el-index">
          <div v-if="currentItem" title="Video element index">{{ currentItem.elIndex }}</div>
        </div>
        <div class="row" id="video-anchor"></div>
        <div class="error">
          <div v-if="error">ERROR {{error.message}}. {{error.error}}</div>
        </div>
        <div class="loading">
          <div v-if="loading">Loading...</div>
        </div>
      </div>

      <div class="stats row">
        <div>
          <div class="stat" v-if="currentItem">
            <div>
              <span v-for="(_, index) in overview" :key="index">
                <span v-if="index > 0"> - </span>
                <span :class="index === currentItem.index ? 'selected-index': ''">{{index}}</span>
              </span>
            </div>
            <div>{{ currentItem.title }}</div>
            <div class="preloading">
              <b>Preloading:</b>
              <div v-if="nextItem">Next: {{ nextItem.title }} (<span :class="nextItem.stateClass">{{nextItem.state}}</span>)</div>
              <div v-if="!nextItem">Next: ---</div>
              <div v-if="prevItem">Prev: {{ prevItem.title }} (<span :class="prevItem.stateClass">{{prevItem.state}}</span>)</div>
              <div v-if="!prevItem">Prev: ---</div>
            </div>
            <CirclePlot v-if="!error && !loading && getPlayer()" :player="getPlayer()"/>
          </div>
        </div>
      </div>

    </section>
  `,
  data () {
    return {
      overview: PLAYLIST_CONFIGS,
      loading: false,
      canPlayFirst: true,
      canPlayNext: false,
      canPlayPrev: false,
      canDestroy: true,
      currentItem: null,
      nextItem: null,
      prevItem: null,
      error: null,
    };
  },
  watch: {},
  mounted () {
    // Create a new playlist attached to the #video-anchor element. The playlist
    // will append <video> elements to it
    playlist = new clpp.plist.McPlaylist({
      anchorId: 'video-anchor',
      preloadMedia: true,
      autoplayNext: true,
      config: {
        // license: '',
      },
    });

    // Set playlist items and configure the position of the item to start from
    playlist.setItems(PLAYLIST_CONFIGS);
    playlist.setIndex(0);

    // Listen to updates (e.g. triggered when a source is pre-loaded)
    playlist.on('update', () => {
      this.updateState();
    });

    // Listen to errors occurring in the background (e.g. an error during preloading)
    playlist.on('error', onPlaylistError);
  },
  destroyed () {
    playlist.off('error', onPlaylistError);
  },
  methods: {
    getPlayer () {
      return this.player_;
    },
    async playFirst () {
      try {
        this.loading = true;
        await playlist.playFirst();
        this.error = null;
      } catch (error) {
        this.error = {
          error,
          message: 'Failed to play first',
        };
      } finally {
        this.loading = false;
      }
      this.canPlayFirst = false;
      this.updateState();
    },
    async playNext () {
      try {
        this.loading = true;
        await playlist.playNext();
        this.error = null;
      } catch (error) {
        this.error = {
          error,
          message: 'Failed to play next',
        };
      } finally {
        this.loading = false;
      }
      this.updateState();
    },
    async playPrev () {
      try {
        this.loading = true;
        await playlist.playPrev();
        this.error = null;
      } catch (error) {
        this.error = {
          error,
          message: 'Failed to play next',
        };
      } finally {
        this.loading = false;
      }
      this.updateState();
    },
    /**
     * Seek 5 seconds before the end of the current video
     */
    async seekToEnd () {
      if (!this.player_) {
        return;
      }

      const d = this.player_.getDuration();
      this.player_.seek(d - 5);
    },
    /**
     * Seek 5 seconds after the end of the current video
     */
    async seekAfterEnd () {
      if (!this.player_) {
        return;
      }

      const d = this.player_.getDuration();
      this.player_.seek(d + 5);
    },
    destroy () {
      this.canPlayFirst = false;
      this.canPlayNext = false;
      this.canPlayPrev = false;
      this.canDestroy = false;
      playlist.destroy();
    },
    updateState () {
      this.canPlayNext = playlist.hasNext();
      this.canPlayPrev = playlist.hasPrev();

      const player = playlist.getCurrentPlayer();
      window.player = player;

      if (!this.player_ || player !== this.player_) {
        if (this.player_) {
          this.player_.off('error', onPlaybackError);
        }

        // Attach listeners to the player instance
        // Listen to errors occurring during playback
        player.on('error', onPlaybackError);

        console.info(`Active player is now ${player.getSurface().getUniqueCssClass()}`);
        this.player_ = player;
      }

      this.currentItem = convertItem(playlist.getCurrent());
      this.nextItem = convertItem(playlist.getNext());
      this.prevItem = convertItem(playlist.getPrev());
    },
  }
};

</script>

<style scoped lang="scss">
  @import 'styles.scss';
</style>
